import AiPromptItem from "@components/Actions/CatalogItems/AiPromptItem";
import CatalogItem from "@components/Actions/CatalogItems/Base";
import CatalogMoveItem, { CatalogMoveSelectItem } from "@components/Actions/CatalogItems/CatalogMoveItem";
import ExportCatalogItem from "@components/Actions/CatalogItems/ExportCatalogItem";
import ExportMenuItem from "@components/Actions/CatalogItems/ExportMenuItem";
import FavoriteMenuItem from "@components/Actions/CatalogItems/FavoriteMenuItem";
import GesCloudRepositoryPermissionItem from "@components/Actions/CatalogItems/GesCloudRepositoryPermissionItem";
import HealthcheckItem from "@components/Actions/CatalogItems/HealthcheckItem";
import NotesItem from "@components/Actions/CatalogItems/NotesItem";
import ReportItem from "@components/Actions/CatalogItems/ReportItem";
import RepositoryPermissionItem from "@components/Actions/CatalogItems/RepositoryPermissionItem";
import ShareCatalogItem from "@components/Actions/CatalogItems/ShareCatalogItem";
import { getCatalogDeleteOptions } from "@components/Actions/useCatalogDeleteOptions";
import Icon from "@components/Atoms/Icon";
import { PlatformServiceNew } from "@core-ui/PlatformService";
import type { CatalogMoveActionRenderProps } from "@ext/catalog/actions/move/components/CatalogMoveAction";
import CatalogPropsTrigger from "@ext/catalog/actions/propsEditor/components/CatalogPropsTrigger";
import DeleteCatalog from "@ext/catalog/actions/propsEditor/components/DeleteCatalog";
import DeleteCatalogFromSpace from "@ext/catalog/actions/propsEditor/components/DeleteCatalogFromSpace";
import t from "@ext/localization/locale/translate";
import openCloudModal from "@ext/static/components/openCloudModal";
import { feature } from "@ext/toggleFeatures/features";
import { ExportFormat } from "@ext/wordExport/components/ItemExport";
import { workspaceToolsCatalogMenu } from "@ext/workspaceTools/components"; // workspace-tools seam
import type { CoreMenuItemId } from "@gramax/sdk/ui";
import { DropdownMenuLabel, DropdownMenuSeparator } from "@ui-kit/Dropdown";
import type { ReactNode } from "react";
import DownloadZip from "../DownloadZip";
import ShowInExplorer from "../ShowInExplorer";
import type { CatalogActionsContextValue } from "./CatalogActionsContext";

type SeparatorId = `separator-${string}`;

type CoreMenuItemIdNew =
	| "navigation-title"
	| SeparatorId
	| "repository-permission"
	| "ges-cloud-repository-permission"
	| "fragments"
	| "agent-skills"
	| "workspace-tools"
	| "report"
	| "delete-catalog-from-space";

export type CoreMenuItemIdApp = Exclude<CoreMenuItemId, "view-favorites"> | CoreMenuItemIdNew;

export interface MenuItemPropMap {
	[separatorId: SeparatorId]: ReactNode;
	"navigation-title": ReactNode;
	"catalog-settings": ReactNode;
	"share-ticket": ReactNode;
	"share-catalog": ReactNode;
	"show-in-explorer": ReactNode;
	export: (() => ReactNode) | undefined;
	"export-docx": ReactNode;
	"export-pdf": ReactNode;
	"export-zip": ReactNode;
	"publish-to-cloud": ReactNode;
	"toggle-favorite": ReactNode;
	notes: ReactNode;
	report: ReactNode;
	snippets: ReactNode;
	fragments: ReactNode;
	"agent-skills": ReactNode;
	template: ReactNode;
	"ai-prompt": ReactNode;
	healthcheck: ReactNode;
	"delete-catalog": ReactNode;
	"delete-catalog-from-space": ReactNode;
	"catalog-move": (props: CatalogMoveActionRenderProps) => ReactNode;
	"catalog-move-select": CatalogMoveActionRenderProps;
	"repository-permission": ReactNode;
	"workspace-tools": ReactNode;
	"ges-cloud-repository-permission": ReactNode;
}

export type MenuItemDescriptorApp = {
	[K in CoreMenuItemIdApp]: {
		id: K;
		component: (props: MenuItemPropMap[K]) => React.ReactNode;
		children?: MenuItemDescriptorApp[];
		visible?: boolean;
	};
}[CoreMenuItemIdApp];

const isLayoutItem = (id: CoreMenuItemIdApp): boolean => id === "navigation-title" || id.startsWith("separator-");

export const hasCatalogMenuItems = (items: MenuItemDescriptorApp[]): boolean =>
	items.some((item) => item.visible && !isLayoutItem(item.id));

export function buildCatalogMenu(ctx: CatalogActionsContextValue): MenuItemDescriptorApp[] {
	const {
		canConfigure,
		canConfigureCatalog,
		hasWorkspaceGesUrl,
		gesCloudEnabled,
		isStorageConnected,
		isLegacyAiAvailable,
		renderDeleteCatalog,
		isMac,
		isReadOnly,
		cloudServiceUrl,
		hasSource,
		limitedActions,
	} = ctx;
	const platform = PlatformServiceNew;
	const isAnyGes = hasWorkspaceGesUrl || gesCloudEnabled;
	const deleteOptions = getCatalogDeleteOptions({
		canDelete: renderDeleteCatalog,
		isEditorApp: platform.isWeb || platform.isDesktop,
		isSpace: isAnyGes,
		isPublished: hasSource,
		canConfigureCatalog,
	});

	const catalogActionsGroup: MenuItemDescriptorApp[] = [
		{
			id: "catalog-settings",
			component: (children) => <CatalogPropsTrigger>{children}</CatalogPropsTrigger>,
			visible: canConfigure && !isReadOnly,
		},
		{
			id: "share-catalog",
			component: (children) => <ShareCatalogItem copyOnly={isAnyGes}>{children}</ShareCatalogItem>,
			visible: !platform.isDocPortal && hasSource,
		},
		{
			id: "repository-permission",
			component: (children) => <RepositoryPermissionItem>{children}</RepositoryPermissionItem>,
			visible: hasWorkspaceGesUrl && canConfigure && isStorageConnected && (platform.isWeb || platform.isDesktop),
		},
		{
			id: "ges-cloud-repository-permission",
			component: (children) => <GesCloudRepositoryPermissionItem>{children}</GesCloudRepositoryPermissionItem>,
			visible: gesCloudEnabled && canConfigure && isStorageConnected && (platform.isWeb || platform.isDesktop),
		},
		{
			id: "toggle-favorite",
			visible: !platform.isStatic && !platform.isStaticCli,
			component: (children) => <FavoriteMenuItem>{children}</FavoriteMenuItem>,
		},
		{
			id: "catalog-move",
			component: (children) => <CatalogMoveItem>{children}</CatalogMoveItem>,
			visible: !platform.isStatic && !platform.isStaticCli && !platform.isDocPortal,
			children: [
				{
					id: "catalog-move-select",
					component: ({ targetWorkspaceRef, checkAndMove }) => (
						<CatalogMoveSelectItem checkAndMove={checkAndMove} targetWorkspaceRef={targetWorkspaceRef} />
					),
					visible: true,
				},
			],
		},
	];

	const toolsGroup: MenuItemDescriptorApp[] = [
		{
			id: "ai-prompt",
			component: (children) => <AiPromptItem>{children}</AiPromptItem>,
			visible: !isReadOnly && isLegacyAiAvailable,
		},
		{
			id: "notes",
			visible: !isReadOnly,
			component: (children) => <NotesItem>{children}</NotesItem>,
		},
		{
			id: "report",
			visible: !isReadOnly,
			component: (children) => <ReportItem>{children}</ReportItem>,
		},
	] as const;

	const exportGroup: MenuItemDescriptorApp[] = [
		{
			id: "show-in-explorer",
			component: () => <ShowInExplorer />,
			visible: platform.isDesktop,
		},
		{
			id: "export",
			component: (children) => <ExportCatalogItem>{children}</ExportCatalogItem>,
			visible: true,
			children: [
				{
					id: "export-docx",
					component: () => <ExportMenuItem exportFormat={ExportFormat.docx} />,
					visible: true,
				},
				{
					id: "export-pdf",
					component: () => <ExportMenuItem exportFormat={ExportFormat.pdf} />,
					visible: true,
				},
				{
					id: "export-zip",
					component: () => <DownloadZip />,
					visible: true,
				},
			],
		},
		{
			id: "publish-to-cloud",
			component: (children) => (
				<CatalogItem
					renderLabel={(Item) => (
						<Item onSelect={openCloudModal}>
							<Icon code="cloud-upload" />
							{t("cloud.publish-to-cloud")}
						</Item>
					)}
				>
					{children}
				</CatalogItem>
			),
			visible: !isReadOnly && !(isMac && platform.isDesktop) && !!cloudServiceUrl && feature("cloud"),
		},
	];

	const healthcheck: MenuItemDescriptorApp[] = [
		{
			id: "healthcheck",
			visible: !isReadOnly,
			component: (children) => <HealthcheckItem>{children}</HealthcheckItem>,
		},
	];

	const deleteAction: MenuItemDescriptorApp[] = [
		{
			id: "delete-catalog",
			component: (children) => <DeleteCatalog fromDevice={deleteOptions.fromDevice}>{children}</DeleteCatalog>,
			visible: deleteOptions.plain || deleteOptions.fromDevice,
		},
		{
			id: "delete-catalog-from-space",
			component: (children) => <DeleteCatalogFromSpace>{children}</DeleteCatalogFromSpace>,
			visible: deleteOptions.fromSpace,
		},
	];

	const createGroupWithSeparator = (groupName: string, items: MenuItemDescriptorApp[]): MenuItemDescriptorApp[] => {
		return [
			{
				id: `separator-${groupName}`,
				component: () => <DropdownMenuSeparator />,
				visible: items.some((item) => item.visible !== false),
			},
			...items,
		];
	};

	const navigationTitle: MenuItemDescriptorApp = {
		id: "navigation-title",
		component: () => (
			<DropdownMenuLabel className="text-primary-fg">{t("catalog.actions.title")}</DropdownMenuLabel>
		),
		visible: true,
	};

	const workspaceToolsGroup = workspaceToolsCatalogMenu(ctx); // workspace-tools seam

	if (limitedActions)
		return [navigationTitle, ...createGroupWithSeparator("healthcheck", healthcheck), ...workspaceToolsGroup];

	return [
		navigationTitle,
		...createGroupWithSeparator("catalog-actions", catalogActionsGroup),
		...createGroupWithSeparator("tools", toolsGroup),
		...workspaceToolsGroup,
		...createGroupWithSeparator("export", exportGroup),
		...createGroupWithSeparator("healthcheck", healthcheck),
		...createGroupWithSeparator("delete", deleteAction),
	] as const;
}
